import React, { useEffect, useState } from 'react'
import { List, Map } from 'immutable'
import _get from 'lodash/get'
import compose from 'recompose/compose'
import { connect as connectRedux, ReactReduxContext } from 'react-redux'
import { getBoundActions } from '../../actions/index'
import { TYPE_FEATURE, TYPE_PREVIEW } from '../../services/video/index'
import { getAuthIsLoggedIn } from '../../services/auth/index'
import { getSessionStorage, SESSION_STORAGE_SKIP_PRE_ROLL } from '../../services/local-preferences/index'
import { getUserLanguagePrimary } from '../../services/user/index'
import { EN } from '../../services/languages/constants'
import { isSubtypeYogaFitnessMeditation, getPrimaryCourse } from '../../services/content/index'
import { getPrimary } from '../../services/languages/index'
import VideoPlayer from '../VideoPlayer/index'
import VideoPlayerContainer from '../VideoPlayerContainer/index'
import VideoPreRoll from '../VideoPreRoll/index'
import ButtonSignUp, { BUTTON_SIGN_UP_TYPE_BUTTON } from '../ButtonSignUp/index'
import { get as getConfig } from '../../config/index'

const config = getConfig()
const preRollEnabled = _get(config, ['features', 'preRollPoC', 'enabled'])
const preRollMediaId = _get(config, ['features', 'preRollPoC', 'mediaId'])

const MAX_PRE_ROLL_COUNT = 2

const videoStates = {
  PRE_ROLL_PLAYER: 'pre-roll-player',
  VIDEO_PLAYER: 'video-player',
  GEO_NOT_ALLOWED: 'geo-not-allowed',
  NOT_ALLOWED: 'not-allowed',
  ERROR: 'error',
}

function shouldRenderPreRoll (props) {
  const {
    preRollPoC,
    user,
    playerType,
    contentSubtype,
  } = props
  const language = getUserLanguagePrimary(user)
  const isYogaMeditationFitness = isSubtypeYogaFitnessMeditation({ contentSubtype })

  if (language !== EN || playerType !== TYPE_FEATURE || isYogaMeditationFitness) {
    return false
  }

  const skipPreRollPreference = getSessionStorage(SESSION_STORAGE_SKIP_PRE_ROLL, false)
  const mediaId = preRollPoC.get('mediaId', preRollMediaId)
  const count = preRollPoC.get('count', 0)
  return preRollEnabled
    && !skipPreRollPreference
    && (mediaId !== preRollMediaId || count < MAX_PRE_ROLL_COUNT)
}

function getInitialVideoState (props) {
  const {
    detailData,
    preRollPoC,
    user,
    playerType,
  } = props

  if (playerType === TYPE_PREVIEW) {
    const previewMedia = detailData.get('previewMedia')
    return previewMedia ? videoStates.VIDEO_PLAYER : videoStates.ERROR
  }

  const featureMedia = detailData.get('featureMedia')

  if (!featureMedia) {
    return videoStates.ERROR
  }

  const contentSubtype = detailData.get('contentSubtype')
  const videoAllowed = featureMedia.getIn(['contentPolicy', 'accessCheck', 'allowed'])
  const geoAllowed = featureMedia.getIn(['contentPolicy', 'accessCheck', 'countryAllowed'])
  const renderPreRoll = shouldRenderPreRoll({
    preRollPoC,
    user,
    playerType,
    contentSubtype,
  })

  if (!geoAllowed) {
    return videoStates.GEO_NOT_ALLOWED
  }

  if (!videoAllowed) {
    return videoStates.NOT_ALLOWED
  }

  return renderPreRoll ? videoStates.PRE_ROLL_PLAYER : videoStates.VIDEO_PLAYER
}

function DetailPageV3VideoPlayer (props) {
  const {
    detailData,
    user,
    auth,
    apolloClient,
    location,
    history,
    video,
    media,
    userVideo,
    getVideoData,
    getMediaData,
    getUserVideoData,
    resetVideo,
    resetMedia,
    resetUserVideo,
    preRollPoC,
    staticText,
  } = props

  const jwt = auth.get('jwt')
  const isLoggedIn = getAuthIsLoggedIn(auth)
  const reduxContext = React.useContext(ReactReduxContext)
  const store = reduxContext ? reduxContext.store : null
  const path = _get(location, 'pathname')
  const playerType = _get(location, ['query', 'fullplayer']) || null
  const contentId = detailData.get('contentId')
  const mediaId = playerType === TYPE_FEATURE
    ? Number(detailData.getIn(['featureMedia', 'mediaId']))
    : Number(detailData.getIn(['previewMedia', 'mediaId']))
  const isVideoProcessing = video.get('processing')
  const isMediaProcessing = media.get('processing')
  const isUserVideoProcessing = userVideo.get('processing')
  const shouldLoadUserVideo = isLoggedIn && playerType === TYPE_FEATURE
  const userVideoReady = shouldLoadUserVideo ? (contentId === userVideo.get('id')) : true
  const isPlayerReady = (
    video.get('id') === contentId
    && media.get('id') === mediaId
    && userVideoReady
    && !isVideoProcessing
    && !isMediaProcessing
    && !isUserVideoProcessing
  )
  const userLanguage = getPrimary(user.getIn(['data', 'language'], List()))
  const primaryCourse = getPrimaryCourse({ data: detailData, userLanguage })
  const containerStyle = [
    'detail-page-v3__video-player-container',
    `detail-page-v3__video-player-container--${playerType}`,
  ].join(' ')

  const [videoState, setVideoState] = useState(getInitialVideoState({
    detailData,
    preRollPoC,
    user,
    playerType,
  }))
  const [isPlayerActive, setIsPlayerActive] = useState(false)

  const handleSkipPreRoll = () => {
    setVideoState(videoStates.VIDEO_PLAYER)
  }

  const handlePreRollPlay = () => {
    setIsPlayerActive(true)
  }

  useEffect(() => {
    if (contentId && contentId !== video.get('id')
        && !isVideoProcessing
        && videoState === videoStates.VIDEO_PLAYER) {
      getVideoData({
        id: contentId,
        path,
        language: user.getIn(['data', 'language'], List()),
      })
    }
  }, [contentId, video, isVideoProcessing, getVideoData, videoState])

  useEffect(() => {
    if (mediaId && mediaId !== media.get('id')
        && !isMediaProcessing
        && videoState === videoStates.VIDEO_PLAYER) {
      getMediaData({
        id: mediaId,
        path,
        auth,
        user,
      })
    }
  }, [mediaId, media, isMediaProcessing, getMediaData, videoState])

  useEffect(() => {
    if (contentId && contentId !== userVideo.get('id')
        && shouldLoadUserVideo
        && !isUserVideoProcessing
        && videoState === videoStates.VIDEO_PLAYER) {
      getUserVideoData(contentId, path, jwt)
    }
  }, [
    contentId,
    userVideo,
    shouldLoadUserVideo,
    isUserVideoProcessing,
    getUserVideoData,
    jwt,
    videoState,
  ])

  useEffect(() => {
    if (media.size > 0 && !media.getIn(['data', 'bcHLS'], null) && !isMediaProcessing) {
      setVideoState(videoStates.ERROR)
    }
  }, [media, isMediaProcessing])

  useEffect(() => {
    return () => {
      resetVideo()
      resetMedia()
      resetUserVideo()
    }
  }, [])

  return (
    <VideoPlayerContainer
      isPlayerActive={isPlayerActive}
      className={containerStyle}
    >
      {videoState === videoStates.PRE_ROLL_PLAYER && (
        <VideoPreRoll
          mediaId={preRollMediaId}
          onPreRollComplete={handleSkipPreRoll}
          onPreRollPlay={handlePreRollPlay}
        />
      )}
      {videoState === videoStates.VIDEO_PLAYER && isPlayerReady && (
        <VideoPlayer
          store={store}
          location={location}
          history={history}
          apolloClient={apolloClient}
          onVideoPlay={() => setIsPlayerActive(true)}
          onVideoEnded={() => setIsPlayerActive(false)}
          type={playerType}
          skipContainerStyles
          primaryCourse={primaryCourse}
        />
      )}
      {videoState === videoStates.GEO_NOT_ALLOWED && (
        <div className="detail-page-v3__video-player-not-allowed">
          <p className="detail-page-v3__video-player-not-allowed-text">{staticText.get('notAvailableInArea')}</p>
        </div>
      )}
      {videoState === videoStates.NOT_ALLOWED && (
        <div className="detail-page-v3__video-player-not-allowed">
          <p className="detail-page-v3__video-player-not-allowed-text">{staticText.get('noActiveSubscription')}</p>
          <ButtonSignUp
            text={staticText.get('signUpToWatch')}
            type={BUTTON_SIGN_UP_TYPE_BUTTON}
            scrollToTop
          />
        </div>
      )}
      {videoState === videoStates.ERROR && (
        <div className="detail-page-v3__video-player-not-allowed">
          <p className="detail-page-v3__video-player-not-allowed-text">{staticText.get('videoNotAvailable')}</p>
        </div>
      )}
    </VideoPlayerContainer>
  )
}

export default compose(
  connectRedux(
    (state) => ({
      user: state.user,
      auth: state.auth,
      video: state.video,
      media: state.media,
      userVideo: state.userVideo,
      preRollPoC: state.featureTracking.getIn(['data', 'preRollPoC'], Map()) || Map(),
      staticText: state.staticText.getIn(['data', 'detailPageV3', 'data']),
    }),
    (dispatch) => {
      const actions = getBoundActions(dispatch)
      return {
        getVideoData: actions.video.getVideoData,
        getMediaData: actions.media.getMediaData,
        getUserVideoData: actions.userVideo.getUserVideoData,
        resetVideo: actions.video.resetVideo,
        resetMedia: actions.media.resetMedia,
        resetUserVideo: actions.userVideo.resetUserVideo,
        setCommentsVisible: actions.comments.setCommentsVisible,
      }
    },
  ),
)(DetailPageV3VideoPlayer)
